import { useEffect, useState } from 'react';
import type { ApiClient } from '../../api';
import type {
  MsrAspDetail, MsrFeldgeraetZeile, MsrKabel, MsrKabelliste, MsrKlemmenbelegung, MsrKlemmenbelegungZeile,
  MsrSchrankbilanz,
} from './typen';
import { ART, ASP_ART, GEWERK, HERKUNFT, z } from './gemeinsam';
import { SchemaBild } from './SchemaBild';
import { PdfKnopf } from './PdfKnopf';
import { Befunde } from './Befunde';
import { RANG_ASP, msrZiele, useMsrExport } from './kopf';
import {
  DataTable, EmptyState, ErrorState, LoadingState, StatusBadge, Vorbehalte, formatiereZahl,
  type Spalte, type Vorbehalt,
} from '../../muster';

const ART_SCHLUESSEL = ['be', 'ba', 'ae', 'aa', 'ze', 'kom'] as const;

type Feld = MsrAspDetail['felderListe'][number];
type Anlage = MsrAspDetail['anlagenListe'][number];
type Busgeraet = MsrKlemmenbelegung['busgeraete'][number] & { nr: number };

const MONO = { fontFamily: 'monospace', fontSize: 11 } as const;

/**
 * Fachfrage MSR-15 am Kabelabschnitt (TM-8b): Kennzeichen und Aderzuordnung sind ein Vorschlag, ein Kabel ohne
 * Gerätelage hat keine Länge. Steht, bis die Annahmenliste das Kennzeichen als bestätigt führt - bestätigt wird hier
 * nichts.
 */
export const VORBEHALT_MSR15: Vorbehalt = {
  art: 'fachfrage',
  text: 'MSR-15 (Frage 14): Kabelkennzeichen und Aderzuordnung sind ein Vorschlag und nicht bestätigt; ein Kabel ohne '
    + 'Gerätelage hat keine Länge (kein Ansatz), die Summe der Längen ist dann eine untere Schranke.',
  verweis: 'Annahmen und Vorgaben',
};

const FELD_SPALTEN: Spalte<Feld & { lfd: number }>[] = [
  { schluessel: 'nummer', titel: 'Nummer', art: 'zahl', wert: (f) => f.nummer ?? f.lfd },
  {
    schluessel: 'herkunft', titel: 'Herkunft', art: 'status',
    wert: (f) => ((f.herkunft ?? 'modell') === 'erweiterung' ? 'Erweiterung' : 'aus Modell'),
    inhalt: (f) => ((f.herkunft ?? 'modell') === 'erweiterung'
      ? <StatusBadge zustand="warnung" text="Erweiterung" klein />
      : 'aus Modell'),
  },
  { schluessel: 'raumnummer', titel: 'Raum', wert: (f) => f.raumnummer ?? null },
  { schluessel: 'begruendung', titel: 'Begründung', wert: (f) => f.begruendung ?? null },
];

const ANLAGEN_SPALTEN: Spalte<Anlage>[] = [
  { schluessel: 'kennung', titel: 'Kennung', immerSichtbar: true, wert: (a) => a.kennung },
  { schluessel: 'gewerk', titel: 'Gewerk', wert: (a) => a.gewerk },
  { schluessel: 'anlagenart', titel: 'Anlagenart', wert: (a) => a.anlagenart },
  { schluessel: 'datenpunkte', titel: 'Datenpunkte', art: 'zahl', wert: (a) => a.datenpunkte, fuss: 'summe' },
];

const FELDGERAETE_SPALTEN: Spalte<MsrFeldgeraetZeile>[] = [
  { schluessel: 'bezeichnung', titel: 'Bezeichnung', immerSichtbar: true, wert: (f) => f.bezeichnung ?? null },
  { schluessel: 'anlage', titel: 'Anlage', wert: (f) => f.anlageKennung ?? null },
  { schluessel: 'datenpunkte', titel: 'Datenpunkte', art: 'zahl', wert: (f) => f.datenpunkte, fuss: 'summe' },
];

const KLEMMEN_SPALTEN: Spalte<MsrKlemmenbelegungZeile>[] = [
  { schluessel: 'kanal', titel: 'Kanal', art: 'zahl', wert: (k) => k.kanal },
  { schluessel: 'klemme', titel: 'Klemme', wert: (k) => k.klemme, inhalt: (k) => <span style={MONO}>{k.klemme}</span> },
  {
    schluessel: 'aks', titel: 'AKS', wert: (k) => k.aks ?? null,
    inhalt: (k) => (k.aks ? <span style={MONO}>{k.aks}</span> : <em className="schwach">— nicht vergeben —</em>),
  },
  { schluessel: 'bezeichnung', titel: 'Datenpunkt', wert: (k) => k.bezeichnung },
  { schluessel: 'art', titel: 'Art', wert: (k) => ART[k.art] },
  { schluessel: 'signal', titel: 'Signal', wert: (k) => k.signal ?? null },
  { schluessel: 'aggregat', titel: 'Aggregat', wert: (k) => k.aggregat },
  { schluessel: 'raumnummer', titel: 'Raum', wert: (k) => k.raumnummer ?? null },
  { schluessel: 'kabeltyp', titel: 'Kabeltyp', wert: (k) => k.kabeltyp },
];

const BUS_SPALTEN: Spalte<Busgeraet>[] = [
  { schluessel: 'aggregat', titel: 'Aggregat', immerSichtbar: true, wert: (b) => b.aggregat },
  { schluessel: 'aks', titel: 'AKS', wert: (b) => b.aks ?? null, inhalt: (b) => <span style={MONO}>{b.aks ?? '—'}</span> },
  { schluessel: 'anlage', titel: 'Anlage', wert: (b) => b.anlageKennung ?? null },
  { schluessel: 'datenpunkte', titel: 'Datenpunkte', art: 'zahl', wert: (b) => b.datenpunkte, fuss: 'summe' },
];

const KABEL_SPALTEN: Spalte<MsrKabel>[] = [
  { schluessel: 'feldnummer', titel: 'Feld', art: 'zahl', wert: (k) => k.feldnummer },
  { schluessel: 'vonModul', titel: 'von Modul', wert: (k) => k.vonModul ?? null },
  { schluessel: 'vonKlemme', titel: 'Klemmen', wert: (k) => k.vonKlemme ?? null, inhalt: (k) => <span style={MONO}>{k.vonKlemme ?? '—'}</span> },
  { schluessel: 'bisGeraet', titel: 'bis Gerät', wert: (k) => k.bisGeraet ?? null, inhalt: (k) => <span style={MONO}>{k.bisGeraet ?? '—'}</span> },
  { schluessel: 'punkte', titel: 'Punkte', art: 'zahl', wert: (k) => k.punkte, fuss: 'summe' },
  { schluessel: 'adern', titel: 'Adern', art: 'zahl', wert: (k) => k.adern ?? null },
  {
    schluessel: 'laengeM', titel: 'Länge', art: 'zahl', einheit: 'm', nachkomma: 2,
    wert: (k) => k.laengeM ?? null, fuss: { art: 'summe', schranke: true },
  },
  { schluessel: 'kabeltyp', titel: 'Kabeltyp', wert: (k) => k.kabeltyp },
];

/**
 * Das Detail eines ASP — Reiter „Schaltschränke / ASPs", unter der Tabelle.
 * Felder, Anlagen, Feldgeräte (nach Aggregat gruppiert), Leistungsbilanz,
 * Formular „Kapazität pflegen", Knopf „ASP-Blatt". Klemmenbelegung, Einstellwerte
 * und Kabel des ASP als Excel stehen seit TM-8a in der Exportleiste im Kopf.
 */
export function AspDetail({
  api, projektId, asp, onGespeichert, kabelBestaetigt = false,
}: {
  api: ApiClient;
  projektId: string;
  asp: MsrAspDetail;
  onGespeichert: () => void;
  /** MSR-15: ob die Annahmenliste das Kabelkennzeichen als bestätigt führt; sonst steht der Vorbehalt. */
  kabelBestaetigt?: boolean;
}) {
  const [formular, setFormular] = useState<{
    be: string; ba: string; ae: string; aa: string; ze: string; kom: string;
    reserveProzent: string; fabrikat: string; baureihe: string; begruendung: string;
  }>({
    be: String(asp.kapazitaet.be), ba: String(asp.kapazitaet.ba),
    ae: String(asp.kapazitaet.ae), aa: String(asp.kapazitaet.aa),
    ze: String(asp.kapazitaet.ze), kom: String(asp.kapazitaet.kom),
    reserveProzent: String(asp.reserveProzent), fabrikat: '', baureihe: '', begruendung: '',
  });
  const [speichertLaeuft, setSpeichertLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [schemaOffen, setSchemaOffen] = useState(false);
  const [schaltplanOffen, setSchaltplanOffen] = useState(false);

  // Klemmenbelegung — je ASP-Detail neu geladen (Ergänzung 07.09.2026,
  // Spezifikation 12.3).
  const [klemmen, setKlemmen] = useState<MsrKlemmenbelegung | null>(null);
  const [klemmenFehler, setKlemmenFehler] = useState<string | null>(null);
  const [offenModule, setOffenModule] = useState<Set<string>>(new Set());

  // Schrankbilanz (Module, Verlustleistung, Erwärmung nach DIN EN 61439-1)
  // und Kabelliste — beide seit Migration 0500 aus eigenen Tabellen.
  const [bilanz, setBilanz] = useState<MsrSchrankbilanz | null>(null);
  const [kabel, setKabel] = useState<MsrKabelliste | null>(null);

  useEffect(() => {
    setKlemmen(null);
    setKlemmenFehler(null);
    api.msrKlemmen(projektId, asp.id).then(setKlemmen)
      .catch((e) => setKlemmenFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projektId, asp.id]);

  useEffect(() => {
    setBilanz(null);
    setKabel(null);
    // Beide Angaben entstehen erst mit einem Lauf, der schreibt — fehlen sie,
    // bleibt der Abschnitt aus (kein Fehlerkasten für einen leeren Bestand).
    api.msrSchrankbilanz(projektId, asp.id).then(setBilanz).catch(() => setBilanz(null));
    api.msrKabel(projektId, asp.id).then(setKabel).catch(() => setKabel(null));
  }, [api, projektId, asp.id]);

  const moduleUmschalten = (modul: string) => setOffenModule((o) => {
    const neu = new Set(o);
    if (neu.has(modul)) neu.delete(modul); else neu.add(modul);
    return neu;
  });

  useMsrExport(RANG_ASP, () => [
    msrZiele.klemmenbelegung(api, projektId, asp),
    msrZiele.einstellwerteAsp(api, projektId, asp),
    msrZiele.kabel(api, projektId, asp),
  ], [api, projektId, asp.id, asp.kennung]);

  const speichern = async () => {
    if (!formular.begruendung.trim()) {
      setFehler('Ohne Begründung wird nichts gespeichert.');
      return;
    }

    setSpeichertLaeuft(true);
    setFehler(null);
    try {
      await api.msrAspKapazitaetSetzen(projektId, asp.id, {
        be: Number(formular.be), ba: Number(formular.ba), ae: Number(formular.ae),
        aa: Number(formular.aa), ze: Number(formular.ze), kom: Number(formular.kom),
        reserveProzent: Number(formular.reserveProzent),
        fabrikat: formular.fabrikat || undefined,
        baureihe: formular.baureihe || undefined,
        begruendung: formular.begruendung,
      });
      onGespeichert();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setSpeichertLaeuft(false);
    }
  };

  const felder = asp.felderListe.map((f, i) => ({ ...f, lfd: i + 1 }));
  const busgeraete: Busgeraet[] = (klemmen?.busgeraete ?? []).map((b, i) => ({ ...b, nr: i }));

  return (
    <div className="karte" style={{ marginTop: 12 }}>
      <div className="karte-titel">
        <h3 style={{ margin: 0 }}>{asp.kennung}</h3>
        <StatusBadge zustand="neutral" text={ASP_ART[asp.art]} klein />
        {asp.ueberKapazitaet && <StatusBadge zustand="fehler" text="über Kapazität" klein />}
        <span style={{ marginLeft: 'auto' }}>
          <PdfKnopf api={api} projektId={projektId} art="aspblatt" ziel="asp" zielId={asp.id}
            label="ASP-Blatt" />
        </span>
      </div>

      <div className="feldgitter">
        <div><div className="feldname">Gewerk</div><div>{asp.gewerk ? GEWERK[asp.gewerk as keyof typeof GEWERK] ?? asp.gewerk : '—'}</div></div>
        <div><div className="feldname">Geschoss</div><div>{asp.geschoss ?? '—'}</div></div>
        <div><div className="feldname">Quadrant</div><div>{asp.quadrant ?? '—'}</div></div>
        <div><div className="feldname">Raum</div><div>{asp.raumname ?? asp.raumnummer ?? '—'}</div></div>
        <div><div className="feldname">Felder</div><div title={asp.felderBegruendung ?? undefined}>
          {(asp.felderErweiterung ?? 0) > 0
            ? <>{asp.felderModell ?? asp.felder} + <span style={{ color: 'var(--warnung)', fontWeight: 600 }}>{asp.felderErweiterung}</span></>
            : z(asp.felder)}
        </div></div>
        <div><div className="feldname">Einspeisung</div><div>{asp.einspeisung ?? '—'}</div></div>
        {asp.leistungW !== undefined && (
          <div><div className="feldname">Leistung</div><div>{z(asp.leistungW)} W</div></div>
        )}
      </div>

      <h4>Schrankfelder</h4>
      <DataTable
        name="Schrankfelder"
        spalten={FELD_SPALTEN}
        zeilen={felder}
        schluessel={(f) => f.id}
        dicht
        suche={false}
        spaltenwahl={false}
        leer={<EmptyState klein titel="Keine Felder." erklaerung="Die Felder kommen aus der Elektro-Schrankliste." />}
      />
      <p className="schwach" style={{ fontSize: 11 }}>
        Erweiterungsfelder tragen dieselbe ASP-Nummer.
      </p>

      <h4>Anlagen</h4>
      <DataTable
        name="Anlagen"
        spalten={ANLAGEN_SPALTEN}
        zeilen={asp.anlagenListe}
        schluessel={(a) => a.id}
        dicht
        ausfuhr={asp.anlagenListe.length > 20}
        ausfuhrDateiname={`msr-anlagen-${asp.kennung}`}
        leer={<EmptyState klein titel="Keine Anlagen." erklaerung="Diesem Schrank ist keine GA-Anlage zugeordnet." />}
      />

      <h4>Feldgeräte nach Aggregat</h4>
      <DataTable
        name="Feldgeräte"
        spalten={FELDGERAETE_SPALTEN}
        zeilen={asp.feldgeraeteListe}
        schluessel={(f) => f.id}
        gruppierung={{ nach: (f) => f.aggregat }}
        dicht
        ausfuhr={asp.feldgeraeteListe.length > 20}
        ausfuhrDateiname={`msr-feldgeraete-${asp.kennung}`}
        leer={<EmptyState klein titel="Keine Feldgeräte." erklaerung="Ohne Anlage mit Bauteilen gibt es kein Feldgerät." />}
      />

      <div className="karte-titel" style={{ marginTop: 18 }}>
        <h4 style={{ margin: 0 }}>Klemmenbelegung</h4>
      </div>

      {klemmenFehler && (
        <ErrorState klein meldung="Die Klemmenbelegung konnte nicht geladen werden." technisch={klemmenFehler} />
      )}
      {!klemmen && !klemmenFehler && <LoadingState text="Klemmenbelegung abrufen …" zeilen={4} />}

      {klemmen && klemmen.module.map((m) => (
        <div key={m.modul} style={{ marginBottom: 6 }}>
          <button type="button" className="baum-ebene" style={{ width: '100%' }}
            aria-expanded={offenModule.has(m.modul)}
            onClick={() => moduleUmschalten(m.modul)}>
            <span>{offenModule.has(m.modul) ? '▾' : '▸'}</span>
            <span>Modul {m.modul}</span>
            <span className="schwach">{ART[m.art]}</span>
            <span className="schwach" style={{ marginLeft: 'auto' }}>
              {z(m.belegt)}/{z(m.kanaele)} belegt
              {!!m.nutzbar && m.nutzbar < m.kanaele && (
                <> · {z(m.kanaele - m.nutzbar)} Kanäle Reserve</>
              )}
            </span>
          </button>

          {offenModule.has(m.modul) && (
            <div style={{ padding: '6px 0 6px 20px' }}>
              <DataTable
                name={`Klemmen Modul ${m.modul}`}
                spalten={KLEMMEN_SPALTEN}
                zeilen={m.zeilen}
                schluessel={(k) => k.klemme}
                dicht
                suche={false}
                leer={<EmptyState klein titel="Keine Klemmen belegt." erklaerung="Auf diesem Modul liegt kein Datenpunkt." />}
              />
            </div>
          )}
        </div>
      ))}

      {klemmen && klemmen.module.length === 0 && (
        <EmptyState klein titel="Keine Module."
          erklaerung="Der Schrank trägt keinen Datenpunkt an einer Klemme - Module vergibt die Schrankrechnung." />
      )}

      {klemmen && (
        <>
          <h4>Busgeräte</h4>
          <DataTable
            name="Busgeräte"
            spalten={BUS_SPALTEN}
            zeilen={busgeraete}
            schluessel={(b) => `${b.nr}`}
            dicht
            ausfuhr={busgeraete.length > 20}
            ausfuhrDateiname={`msr-busgeraete-${asp.kennung}`}
            leer={<EmptyState klein titel="Keine Busgeräte." erklaerung="Kein Gerät dieses Schranks ist über einen Bus angebunden." />}
          />
        </>
      )}

      {bilanz && (
        <>
          <div className="karte-titel" style={{ marginTop: 18 }}>
            <h4 style={{ margin: 0 }}>Schrankbilanz (DIN EN 61439-1)</h4>
            {bilanz.luefterErforderlich && <StatusBadge zustand="warnung" text="Lüftung erforderlich" klein />}
          </div>

          <div className="feldgitter">
            <div><div className="feldname">Module</div><div>
              {z(bilanz.moduleGesamt)}
              <span className="schwach"> ({z(bilanz.moduleBe)} BE / {z(bilanz.moduleBa)} BA /{' '}
                {z(bilanz.moduleAe)} AE / {z(bilanz.moduleAa)} AA)</span>
            </div></div>
            <div><div className="feldname">Kanalreserve</div><div>
              {bilanz.kanalreserveProzent == null ? '—' : `${z(bilanz.kanalreserveProzent)} %`}
            </div></div>
            <div><div className="feldname">Punkte</div><div>
              {z(bilanz.klemmenpunkteZahl)} an Klemmen
              <span className="schwach"> · {z(bilanz.buspunkteZahl)} am Bus</span>
            </div></div>
            <div><div className="feldname">Anschlussleistung</div><div style={{ fontWeight: 600 }}>
              {bilanz.anschlussleistungW == null ? '—' : `${z(bilanz.anschlussleistungW)} W`}
              {bilanz.verteilerKennzeichen && (
                <span className="schwach"> an {bilanz.verteilerKennzeichen}</span>
              )}
            </div></div>
            {/*
              Der Abgang, auf dem diese Leistung in der Elektro-Verteilerbilanz
              mitzaehlt. Ohne ihn waere die Zahl darueber eine Angabe ohne
              Folgen — genau der Zustand nach Befund M-13.
            */}
            <div><div className="feldname">Abgang im Verteiler</div><div>
              {bilanz.abgang == null
                ? <span className="schwach">—&nbsp;noch nicht gebildet&nbsp;—</span>
                : (
                  <>
                    <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {bilanz.abgang.kennzeichen}
                    </span>
                    <span className="schwach">
                      {' '}· {bilanz.abgang.schutzorgan ?? '—'}
                      {bilanz.abgang.leitungsart ? ` · ${bilanz.abgang.leitungsart}` : ''}
                    </span>
                  </>
                )}
            </div></div>
            <div><div className="feldname">Verlustleistung</div><div style={{ fontWeight: 600 }}>
              {bilanz.verlustleistungW == null ? '—' : `${z(bilanz.verlustleistungW)} W`}
            </div></div>
            <div><div className="feldname">Netzteile</div><div>
              {bilanz.netzteileZahl ?? '—'}
              {bilanz.netzteilVerlustW != null && (
                <span className="schwach"> · Verlust {z(bilanz.netzteilVerlustW)} W</span>
              )}
            </div></div>
            <div><div className="feldname">Erwärmung</div><div>
              {bilanz.uebertemperaturK == null ? '—' : `${z(bilanz.uebertemperaturK)} K`}
              {bilanz.innentemperaturC != null && (
                <span className={bilanz.luefterErforderlich ? '' : 'schwach'}>
                  {' '}→ {z(bilanz.innentemperaturC)} °C innen
                </span>
              )}
            </div></div>
            <div><div className="feldname">zulässig</div><div>
              {bilanz.innentemperaturMaxC == null ? '—' : `${z(bilanz.innentemperaturMaxC)} °C`}
              {bilanz.umgebungstemperaturC != null && (
                <span className="schwach"> bei {z(bilanz.umgebungstemperaturC)} °C außen</span>
              )}
            </div></div>
            {bilanz.luefterVolumenstromM3h != null && (
              <div><div className="feldname">Filterlüfter</div>
                <div>{z(bilanz.luefterVolumenstromM3h)} m³/h</div></div>
            )}
          </div>

          {bilanz.waermebilanz && (
            <p className="schwach" style={{ fontSize: 12 }}>{bilanz.waermebilanz}</p>
          )}

          {bilanz.bussegmente.length > 0 && (
            <div className="hinweis warnung">
              {bilanz.bussegmente.map((b, i) => <div key={i}>{b.text}</div>)}
            </div>
          )}

          {bilanz.abgang && (
            <p className="schwach" style={{ fontSize: 11 }}>
              Abgang {bilanz.abgang.kennzeichen}: {bilanz.abgang.schutzorgan ?? '—'}
              {bilanz.abgang.bemessungsstrom != null && ` (${z(bilanz.abgang.bemessungsstrom)} A)`}
              {' · '}{bilanz.abgang.leitungsart ?? '—'}
              {bilanz.abgang.betriebsstrom != null
                && ` · I_b ${formatiereZahl(bilanz.abgang.betriebsstrom, 2)} A`}
              {bilanz.abgang.nachweisHerkunft === 'vorschlag'
                && ' — gerechnet nach DIN VDE 0100-430/0298-4 und NICHT bestätigt.'}
            </p>
          )}

          <p className="schwach" style={{ fontSize: 11 }}>
            Netzteilwirkungsgrad, Modulverluste, Kühlfläche, zulässige Innentemperatur und
            Umgebungstemperatur sind unbestätigte Parameter (Fragen F-03/F-04/F-05) — sie
            stehen als Einstellungen mit Herkunft „geschätzt“.
          </p>
        </>
      )}

      {kabel && kabel.zeilen.length > 0 && (
        <section data-abschnitt="kabel">
          <div className="karte-titel" style={{ marginTop: 18 }}>
            <h4 style={{ margin: 0 }}>Kabel</h4>
            <span className="schwach" style={{ marginLeft: 8 }}>
              {z(kabel.gesamt)} Kabel · {kabel.ohneLaenge > 0 ? '≥ ' : ''}{formatiereZahl(kabel.summeLaengeM, 2)} m
              {kabel.ohneLaenge > 0 && <> · {z(kabel.ohneLaenge)} ohne Länge</>}
            </span>
          </div>
          {!kabelBestaetigt && (
            <Vorbehalte eintraege={[{ ...VORBEHALT_MSR15, ziel: `/projekte/${projektId}/msr/annahmen` }]} />
          )}
          <DataTable
            name="Kabel"
            spalten={KABEL_SPALTEN}
            zeilen={kabel.zeilen}
            schluessel={(k) => k.id}
            dicht
            ausfuhr
            ausfuhrDateiname={`msr-kabel-${asp.kennung}`}
            leer={<EmptyState klein titel="Keine Kabel." erklaerung="Kabel vergibt die Schrankrechnung je Feldgerät." />}
          />
        </section>
      )}

      <Befunde api={api} projektId={projektId} ziel={asp.id}
        titel="Befunde dieses Schranks"
        leerText="Keine Befunde an diesem ASP." />

      {asp.leistungsbilanz && (
        <>
          <h4>Leistungsbilanz</h4>
          <div className="feldgitter">
            <div><div className="feldname">Automationsstation</div><div>{z(asp.leistungsbilanz.automationsstationW)} W</div></div>
            <div><div className="feldname">Module</div><div>{z(asp.leistungsbilanz.moduleW)} W</div></div>
            <div><div className="feldname">Feldgeräte</div><div>{z(asp.leistungsbilanz.feldgeraeteVa)} VA</div></div>
            <div><div className="feldname">Netzteil</div><div>{z(asp.leistungsbilanz.netzteilVa)} VA</div></div>
            <div><div className="feldname">Summe</div><div style={{ fontWeight: 600 }}>{z(asp.leistungsbilanz.summeW)} W</div></div>
          </div>
          <p className="schwach" style={{ fontSize: 12 }}>{asp.leistungsbilanz.berechnung}</p>
        </>
      )}

      <h4>Kapazität pflegen</h4>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {ART_SCHLUESSEL.map((a) => (
          <label key={a} style={{ fontSize: 11 }}>
            {a.toUpperCase()}<br />
            <input type="number" min={0} style={{ width: 60 }}
              value={formular[a]}
              onChange={(e) => setFormular((f) => ({ ...f, [a]: e.target.value }))} />
          </label>
        ))}
        <label style={{ fontSize: 11 }}>
          Reserve %<br />
          <input type="number" min={0} max={100} style={{ width: 60 }}
            value={formular.reserveProzent}
            onChange={(e) => setFormular((f) => ({ ...f, reserveProzent: e.target.value }))} />
        </label>
        <label style={{ fontSize: 11 }}>
          Fabrikat<br />
          <input type="text" style={{ width: 120 }} value={formular.fabrikat}
            onChange={(e) => setFormular((f) => ({ ...f, fabrikat: e.target.value }))} />
        </label>
        <label style={{ fontSize: 11 }}>
          Baureihe<br />
          <input type="text" style={{ width: 120 }} value={formular.baureihe}
            onChange={(e) => setFormular((f) => ({ ...f, baureihe: e.target.value }))} />
        </label>
        <label style={{ fontSize: 11, flex: '1 1 220px' }}>
          Begründung *<br />
          <input type="text" style={{ width: '100%', boxSizing: 'border-box' }}
            value={formular.begruendung}
            onChange={(e) => setFormular((f) => ({ ...f, begruendung: e.target.value }))} />
        </label>
        <button type="button" className="knopf betont" disabled={speichertLaeuft}
          onClick={speichern}>
          Speichern
        </button>
      </div>

      <div className="schwach" style={{ fontSize: 11, marginTop: 4 }}>
        Bisherige Herkunft: {HERKUNFT[asp.kapazitaetHerkunft]}
      </div>

      {fehler && <ErrorState klein titel="Nicht gespeichert." meldung={fehler} />}

      <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
        <button type="button" className="knopf" onClick={() => setSchemaOffen((s) => !s)}>
          ASP-Blatt {schemaOffen ? 'ausblenden' : 'anzeigen'}
        </button>
        <button type="button" className="knopf" onClick={() => setSchaltplanOffen((s) => !s)}>
          Übersichtsschaltplan {schaltplanOffen ? 'ausblenden' : 'anzeigen'}
        </button>
      </div>

      {schemaOffen && (
        <div style={{ marginTop: 8 }}>
          <SchemaBild titel="ASP-Blatt" schluessel={asp.id}
            laden={() => api.msrSchemaAspSvg(projektId, asp.id)} />
        </div>
      )}

      {schaltplanOffen && (
        <div style={{ marginTop: 8 }}>
          <SchemaBild titel="Übersichtsschaltplan" schluessel={`schaltplan-${asp.id}`}
            laden={() => api.msrSchemaAspSchaltplanSvg(projektId, asp.id)} />
        </div>
      )}
    </div>
  );
}
